import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import {
  COMMERCIAL_EMAIL,
  LOGISTICS_EMAIL,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_CLIPBOARD,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from "./helpContactDetails";

const SECTION_COMMERCIAL = "commercial";
const SECTION_LOGISTICS = "logistics";
const SECTION_SUPPORT = "support";

const CONTACT_TRIGGER_PX = 268;
const CONTACT_TOPIC_ROW_GAP_PX = 12;
const CONTACT_ROW_GRID_WIDTH_PX =
  CONTACT_TRIGGER_PX * 3 + CONTACT_TOPIC_ROW_GAP_PX * 2;
const HELP_SECTION_HORIZONTAL_PADDING_PX = 32;
const HELP_LAYOUT_OUTER_WIDTH_PX =
  CONTACT_ROW_GRID_WIDTH_PX + HELP_SECTION_HORIZONTAL_PADDING_PX * 2;

const helpLayoutColumnStyle = {
  minWidth: HELP_LAYOUT_OUTER_WIDTH_PX,
  maxWidth: HELP_LAYOUT_OUTER_WIDTH_PX,
};

const notionUrlFromEnv = () => {
  const raw = process.env.REACT_APP_HELP_NOTION_URL;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  return trimmed;
};

const helpCenterNotionUrl = notionUrlFromEnv();

const pageBlockClassName =
  "w-full bg-white border border-[#D3D3D3] rounded-lg px-8 py-5 flex flex-col gap-4";

const documentationBlockClassName = `${pageBlockClassName} border-[#E2E8F0] bg-gradient-to-br from-white via-[#FAFCFC] to-[#F0F9F9] shadow-sm`;

const topicRowGridStyle = {
  gridTemplateColumns: `repeat(3, ${CONTACT_TRIGGER_PX}px)`,
};

const sectionTitleClassName =
  "text-xl font-bold text-[#0F265C] leading-tight mb-0";

const documentationCtaClassName =
  "inline-flex w-fit shrink-0 items-center justify-center self-start rounded-lg bg-[#00A7A6] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#008f8e] transition-colors no-underline";

const documentationSecondaryCtaClassName =
  "inline-flex w-fit shrink-0 items-center justify-center self-start rounded-lg border border-[#00A7A6] bg-white px-4 py-2.5 text-sm font-semibold text-[#00A7A6] hover:bg-[#E9FBFB] transition-colors no-underline";

const triggerSquareClassName =
  "w-full rounded-xl bg-[#F3F4F6] p-3 flex flex-col items-center justify-center gap-2 border-2 border-transparent cursor-pointer transition-colors hover:border-[#00A7A6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A7A6] focus-visible:ring-offset-2 overflow-y-auto";

const triggerSquareOpenClassName = "border-[#00A7A6] bg-[#E9FBFB]";

const triggerIconClassName =
  "fas text-[#00A7A6] text-5xl leading-none shrink-0 mb-3";

const triggerTitleClassName =
  "self-stretch text-center text-lg font-bold text-[#222222] leading-tight px-1";

const triggerWhenClassName =
  "self-stretch w-full min-w-0 text-center text-xs text-[#6B7280] leading-snug px-1";

const chevronClassName =
  "fas fa-chevron-down text-[#6B7280] text-sm transition-transform duration-200 shrink-0 mt-0.5";

const detailPanelClassName =
  "w-full rounded-2xl border border-[#E2E8F0] bg-gradient-to-b from-white to-[#F8FAFB] px-5 py-5 sm:px-6 sm:py-6 shadow-sm flex flex-col gap-4";

const bodyCopyClassName = "text-sm text-[#374151] leading-relaxed mb-0";

const panelTitleClassName =
  "text-lg font-bold text-[#0F265C] tracking-tight mb-0";

const copyButtonClassName =
  "shrink-0 p-2 rounded-md text-[#64748B] hover:text-[#00A7A6] hover:bg-[#E9FBFB] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A7A6]";

const contactDetailEmailSectionClassName =
  "mt-1 flex flex-col gap-2 border-t border-[#E5E7EB] pt-4";

const contactDetailFieldLabelClassName =
  "text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-0";

const contactDetailInsetCardClassName =
  "rounded-lg border border-[#E5E7EB] bg-white/80 px-3 py-3";

const contactDetailHighlightCardClassName =
  "rounded-lg border border-[#E5E7EB] bg-white/90 px-3 py-3 flex flex-col gap-2";

const contactDetailCopyRowClassName = "flex flex-wrap items-center gap-2";

const contactDetailCopyRowLinkClassName =
  "text-sm font-semibold text-[#00A7A6] hover:text-[#008f8e] break-all min-w-0 flex-1";

const EmailCopyRow = ({ email, onCopy, t, footer }) => (
  <div className={contactDetailHighlightCardClassName}>
    <div className={contactDetailCopyRowClassName}>
      <a href={`mailto:${email}`} className={contactDetailCopyRowLinkClassName}>
        {email}
      </a>
      <button
        type="button"
        className={copyButtonClassName}
        onClick={() => onCopy(email)}
        aria-label={t("help_center_copy_email_aria")}
      >
        <i className="fas fa-copy text-sm" aria-hidden />
      </button>
    </div>
    {footer}
  </div>
);

const PhoneCopyRow = ({ telHref, displayText, onCopy, t, footer }) => (
  <div className={contactDetailHighlightCardClassName}>
    <div className={contactDetailCopyRowClassName}>
      <a href={telHref} className={contactDetailCopyRowLinkClassName}>
        {displayText}
      </a>
      <button
        type="button"
        className={copyButtonClassName}
        onClick={onCopy}
        aria-label={t("help_center_copy_phone_aria")}
      >
        <i className="fas fa-copy text-sm" aria-hidden />
      </button>
    </div>
    {footer}
  </div>
);

const ContactDetailEmailSection = ({ email, onCopy, t, emailBlockFooter }) => (
  <div className={contactDetailEmailSectionClassName}>
    <p className={contactDetailFieldLabelClassName}>
      {t("help_center_detail_email_label")}
    </p>
    <EmailCopyRow
      email={email}
      onCopy={onCopy}
      t={t}
      footer={emailBlockFooter}
    />
  </div>
);

const ContactTrigger = ({
  sectionId,
  iconClass,
  title,
  when,
  isOpen,
  onToggle,
  ariaControlsId,
}) => (
  <button
    type="button"
    style={{ height: `${CONTACT_TRIGGER_PX}px` }}
    className={`${triggerSquareClassName} ${isOpen ? triggerSquareOpenClassName : ""}`}
    onClick={() => onToggle(sectionId)}
    aria-expanded={isOpen}
    aria-controls={ariaControlsId}
  >
    <i className={`${triggerIconClassName} ${iconClass}`} aria-hidden />
    <span className={triggerTitleClassName}>{title}</span>
    <span className={triggerWhenClassName}>{when}</span>
    <i
      className={`${chevronClassName} ${isOpen ? "rotate-180" : ""}`}
      aria-hidden
    />
  </button>
);

const Help = () => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState(null);
  const helpLayoutRef = useRef(null);
  const [helpLayoutWidthPx, setHelpLayoutWidthPx] = useState(0);
  const [showAdminLayoutWidth, setShowAdminLayoutWidth] = useState(false);

  useEffect(() => {
    const readAdminLayout = () => {
      const viewMode = Cookies.get("viewMode") || "client";
      const email = Cookies.get("userEmail") || "";
      const isAkanthasAccount = email.includes("@akanthas.com");
      setShowAdminLayoutWidth(isAkanthasAccount && viewMode === "admin");
    };
    readAdminLayout();
    window.addEventListener("viewModeChanged", readAdminLayout);
    return () => window.removeEventListener("viewModeChanged", readAdminLayout);
  }, []);

  useEffect(() => {
    if (!showAdminLayoutWidth) {
      setHelpLayoutWidthPx(0);
      return;
    }
    const el = helpLayoutRef.current;
    if (!el) return;
    const update = () => setHelpLayoutWidthPx(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [showAdminLayoutWidth]);

  const copyHelpEmail = useCallback(
    async (email) => {
      try {
        await navigator.clipboard.writeText(email);
        toast.success(t("help_center_email_copied"));
      } catch {
        toast.error(t("help_center_email_copy_failed"));
      }
    },
    [t],
  );

  const copyHelpPhone = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_PHONE_CLIPBOARD);
      toast.success(t("help_center_phone_copied"));
    } catch {
      toast.error(t("help_center_phone_copy_failed"));
    }
  }, [t]);

  const toggleSection = (id) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className="settings-page-wrapper">
      <PageHeader title={t("Help center")} />
      <div className="settings-content-wrapper">
        {showAdminLayoutWidth && helpLayoutWidthPx > 0 ? (
          <div className="text-center text-[11px] text-gray-400 mb-2">
            {t("Largeur totale")} : {helpLayoutWidthPx}px
          </div>
        ) : null}
        <div className="w-full min-w-0 overflow-x-auto">
          <div
            ref={helpLayoutRef}
            className="mx-auto flex w-full flex-col gap-5"
            style={helpLayoutColumnStyle}
          >
            <section
              className={documentationBlockClassName}
              aria-labelledby="help-notion-heading"
            >
              <div className="flex flex-col gap-4">
                <div className="min-w-0 space-y-3 border-l-4 border-[#00A7A6] pl-4">
                  <h2
                    id="help-notion-heading"
                    className={sectionTitleClassName}
                  >
                    {t("help_center_notion_title")}
                  </h2>
                  <p className={bodyCopyClassName}>
                    {t("help_center_notion_description_p1")}
                  </p>
                  <p className={bodyCopyClassName}>
                    {t("help_center_notion_description_p2")}
                  </p>
                  <p className={bodyCopyClassName}>
                    {t("help_center_notion_description_p3")}
                  </p>
                  {!helpCenterNotionUrl ? (
                    <p className="text-xs text-[#B45309] mb-0 font-medium">
                      {t("help_center_notion_unconfigured")}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {helpCenterNotionUrl ? (
                    <a
                      href={helpCenterNotionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={documentationCtaClassName}
                    >
                      {t("help_center_open_documentation")}
                    </a>
                  ) : null}
                  <Link
                    to="/settings/waste/materials"
                    className={documentationSecondaryCtaClassName}
                  >
                    {t("materialsCatalog.openFromHelp")}
                  </Link>
                </div>
              </div>
            </section>

            <section
              className={pageBlockClassName}
              aria-labelledby="help-contact-heading"
            >
              <h2 id="help-contact-heading" className={sectionTitleClassName}>
                {t("help_center_contact_title")}
              </h2>

              <div className="flex w-full min-w-0 flex-col gap-3">
                <div className="flex w-full justify-center">
                  <div
                    className="grid shrink-0 gap-3"
                    style={topicRowGridStyle}
                  >
                    <ContactTrigger
                      sectionId={SECTION_COMMERCIAL}
                      iconClass="fa-briefcase"
                      title={t("help_center_contact_commercial_title")}
                      when={t("help_center_contact_commercial_when")}
                      isOpen={openSection === SECTION_COMMERCIAL}
                      onToggle={toggleSection}
                      ariaControlsId="help-contact-commercial-panel"
                    />
                    <ContactTrigger
                      sectionId={SECTION_LOGISTICS}
                      iconClass="fa-truck"
                      title={t("help_center_contact_logistics_title")}
                      when={t("help_center_contact_logistics_when")}
                      isOpen={openSection === SECTION_LOGISTICS}
                      onToggle={toggleSection}
                      ariaControlsId="help-contact-logistics-panel"
                    />
                    <ContactTrigger
                      sectionId={SECTION_SUPPORT}
                      iconClass="fa-headset"
                      title={t("help_center_contact_support_title")}
                      when={t("help_center_contact_support_when")}
                      isOpen={openSection === SECTION_SUPPORT}
                      onToggle={toggleSection}
                      ariaControlsId="help-contact-support-panel"
                    />
                  </div>
                </div>

                {openSection === SECTION_COMMERCIAL && (
                  <div
                    id="help-contact-commercial-panel"
                    className={detailPanelClassName}
                    role="region"
                    aria-labelledby="help-contact-commercial-visible-title"
                  >
                    <h3
                      id="help-contact-commercial-visible-title"
                      className={panelTitleClassName}
                    >
                      {t("help_center_contact_commercial_title")}
                    </h3>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_commercial_lead")}
                    </p>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_commercial_more")}
                    </p>
                    <ContactDetailEmailSection
                      email={COMMERCIAL_EMAIL}
                      onCopy={copyHelpEmail}
                      t={t}
                    />
                  </div>
                )}

                {openSection === SECTION_LOGISTICS && (
                  <div
                    id="help-contact-logistics-panel"
                    className={detailPanelClassName}
                    role="region"
                    aria-labelledby="help-contact-logistics-visible-title"
                  >
                    <h3
                      id="help-contact-logistics-visible-title"
                      className={panelTitleClassName}
                    >
                      {t("help_center_contact_logistics_title")}
                    </h3>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_logistics_lead")}
                    </p>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_logistics_more")}
                    </p>
                    <ContactDetailEmailSection
                      email={LOGISTICS_EMAIL}
                      onCopy={copyHelpEmail}
                      t={t}
                    />
                    <div className="flex flex-col gap-2">
                      <p className={contactDetailFieldLabelClassName}>
                        {t("help_center_detail_postal_label")}
                      </p>
                      <div className={contactDetailInsetCardClassName}>
                        <p className="text-sm text-[#374151] leading-relaxed mb-0 whitespace-pre-line">
                          {t("help_center_contact_logistics_address")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {openSection === SECTION_SUPPORT && (
                  <div
                    id="help-contact-support-panel"
                    className={detailPanelClassName}
                    role="region"
                    aria-labelledby="help-contact-support-visible-title"
                  >
                    <h3
                      id="help-contact-support-visible-title"
                      className={panelTitleClassName}
                    >
                      {t("help_center_contact_support_title")}
                    </h3>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_support_lead")}
                    </p>
                    <p className={bodyCopyClassName}>
                      {t("help_center_detail_support_more")}
                    </p>
                    <ContactDetailEmailSection
                      email={SUPPORT_EMAIL}
                      onCopy={copyHelpEmail}
                      t={t}
                      emailBlockFooter={
                        <p className="text-xs text-[#374151] leading-relaxed mb-0">
                          {t("help_center_support_mail_tip")}
                        </p>
                      }
                    />
                    <div className="flex flex-col gap-2">
                      <p className={contactDetailFieldLabelClassName}>
                        {t("help_center_detail_phone_label")}
                      </p>
                      <PhoneCopyRow
                        telHref={SUPPORT_PHONE_TEL}
                        displayText={SUPPORT_PHONE_DISPLAY}
                        onCopy={copyHelpPhone}
                        t={t}
                        footer={
                          <span className="text-xs text-[#6B7280] leading-snug">
                            {t("help_center_support_hours")}
                          </span>
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
